import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { segmentsQueryKeys } from "@/features/segments/api/queryKeys";
import { instance } from "@/lib/axios/instance";
import { GetSegmentsResponse } from "@/types/response/GetSegmentsResponse";

interface UseSegmentsInfiniteParams {
  clientId?: string;
  search?: string;
  orderBy?: string;
  descending?: boolean;
  pageSize?: number;
  enabled?: boolean;
}

export function useSegmentsInfinite({
  clientId,
  search,
  orderBy,
  descending,
  pageSize = 20,
  enabled = true,
}: UseSegmentsInfiniteParams) {
  return useInfiniteQuery<GetSegmentsResponse, AxiosError>({
    queryKey: segmentsQueryKeys.list({
      clientId,
      page: undefined,
      pageSize,
      search,
      orderBy,
      descending,
    }),
    enabled,
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      instance
        .get("/api/v2/Segments", {
          params: {
            clientId,
            page: pageParam,
            pageSize,
            search,
            orderBy,
            descending,
          },
        })
        .then((res) => res.data),
    getNextPageParam: (lastPage, allPages) => {
      const totalRecords = lastPage.totalRecords;
      const totalFetched = allPages.reduce(
        (acc, page) => acc + page.data.length,
        0,
      );
      return totalFetched < totalRecords ? allPages.length + 1 : undefined;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnMount: true, // Overrides the global false so a segment saved elsewhere shows once the list mounts again
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: keepPreviousData,
  });
}
